import {
    ChangeDetectionStrategy,
    Component,
    OnInit,
    ViewChild,
    ViewEncapsulation,
    computed,
    inject,
    input,
    effect,
} from '@angular/core';
import {
    IOnboardingPeriodDateRange,
    IOnboardingReportContainer,
    IOnboardingReportWidget,
    IOnboardingReportWidgetFilter,
} from '../fi-onboarding-report';
import { FiOnboardingReportHelperService } from '../services/fi-onboarding-report-helper.service';
import {
    FiOnboardingReportDateType,
    FiOnboardingReportWidgetType,
    FiOnboardingReportType,
} from '@fi-app/fi-onboarding/fi-onboarding-report/fi-onboarding-report-enums';
import { FiTaskReportDateType } from '@fi-app/task-v2/fi-task-v2-report/fi-task-v2-report-enums';
import { INameIdObj } from '@fi-shared/fi-shared-interfaces';
import { FiOnboardingReportDateService } from '@fi-app/fi-onboarding/fi-onboarding-report/services/fi-onboarding-report-date.service';
import { DeepReadonly } from '@fi-app/shared/types/deep-readonly';
import { Animations } from '@fi-app/shared/utils/animations';
import { FiOnboardingReportService } from '../services/fi-onboarding-report.service';
import { FiReportDetailsBaseDirective } from '@fi-shared/fi-report-widget/fi-report-widget-details-base/fi-report-details-base.directive';
import { IReportAvailableItemsByData } from '@fi-shared/fi-report-table-filter/fi-report-filter-base';
import { FiOnboardingReportTableFilterComponent } from '../shared/fi-onboarding-report-table-filter/fi-onboarding-report-table-filter.component';
import { FiOnboardingReportDataService } from '../services/fi-onboarding-report-data.service';
import {cloneDeep} from "@fi-shared/utils";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {pipe} from "rxjs";

@Component({
    selector: 'fi-onboarding-report-details',
    templateUrl: './fi-onboarding-report-details.component.html',
    styleUrl: './fi-onboarding-report-details.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-onboarding-report-details' },
    standalone: false,
    animations: [Animations.fadeHeight()],
})
export class FiOnboardingReportWidgetDetailsComponent
    extends FiReportDetailsBaseDirective<FiOnboardingReportType, IOnboardingReportWidgetFilter, IOnboardingReportWidget>
    implements OnInit
{
    @ViewChild(FiOnboardingReportTableFilterComponent)
    protected fiOnboardingReportTableFilterComponent?: FiOnboardingReportTableFilterComponent;

    public availableItems = input.required<IOnboardingReportContainer>();
    public reportWidgetType = input.required<FiOnboardingReportWidgetType>();

    private fiOnboardingReportHelperService = inject(FiOnboardingReportHelperService);
    private fiOnboardingReportDateService = inject(FiOnboardingReportDateService);
    private fiOnboardingReportService = inject(FiOnboardingReportService);
    private fiOnboardingReportDataService = inject(FiOnboardingReportDataService);

    protected isFilterEmpty = computed<boolean>(() => {
        return this.fiOnboardingReportHelperService.isFilterEmpty(this.currentFilter());
    });

    protected isResetButtonVisibleLocal = computed<boolean>(() => {
        return this.isOnboardingLocationsWidget() || this.isOnboardingParticipantsWidget()
            ? false
            : this.isResetButtonVisible();
    });

    protected isSaveFilterButtonsVisibleLocal = computed<boolean>(() => {
        return this.isOnboardingLocationsWidget() || this.isOnboardingParticipantsWidget()
            ? false
            : this.isSaveFilterButtonsVisible(this.isFilterChanged());
    });

    protected isReportSubPage = computed<boolean>(() => {
        return this.isOnboardingLocationsWidget() || this.isOnboardingParticipantsWidget();
    });

    protected headerTitle = computed<string>(() => {
        if (this.isOnboardingLocationsWidget()) {
            return this.appTextService.get('onboarding.report_locations_title');
        }
        if (this.isOnboardingParticipantsWidget()) {
            return this.appTextService.get('onboarding.report_participants_title');
        }
        return this.fiOnboardingReportHelperService.getReportName(this.widget());
    });

    private isOnboardingLocationsWidget = computed<boolean>(() => {
        return this.reportWidgetType() === FiOnboardingReportWidgetType.OnboardingLocations;
    });
    private isOnboardingParticipantsWidget = computed<boolean>(() => {
        return this.reportWidgetType() === FiOnboardingReportWidgetType.OnboardingParticipants;
    });

    protected isDateRangeSelectVisible = computed<boolean>(
        () => this.widget()?.ReportType === FiOnboardingReportType.ParticipantsOnboarded,
    );

    protected onboardingDateRange: IOnboardingPeriodDateRange;
    protected skipInitialFilterSetIfNotEmpty = true;
    protected availableItemsByData: IReportAvailableItemsByData = {};

    get savedFiltersLocal(): IOnboardingReportWidgetFilter[] {
        return this.isOnboardingLocationsWidget() || this.isOnboardingParticipantsWidget() ? [] : this.savedFilters;
    }

    public ngOnInit(): void {
        super.ngOnInit();
        this.setCurrentFilter();
        this.subscribeOnFilterItemsChanged();
        this.setDateRange();
    }

    private setCurrentFilter() {
        const currentFilter = this.fiOnboardingReportHelperService.getReportWidgetFilterFromState(this.widget(), {
            reportWidgetType: this.reportWidgetType()
        });
        this.currentFilter.set(currentFilter);
        this.updateFilterChangedStatus();
    }

    private subscribeOnFilterItemsChanged() {
        this.selectedFilterItemsChanged$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.fiOnboardingReportHelperService.saveReportWidgetFilter(this.currentFilter(), this.reportWidgetType());
        })
    }

    private setDateRange(): void {
        if (this.isDateRangeSelectVisible()) {
            this.onboardingDateRange = this.fiOnboardingReportDateService.getDateRange(this.widget().Id);
        }
    }

    protected onCurrentFilterChanged(currentFilter: IOnboardingReportWidgetFilter): void {
        this.currentFilter.set(currentFilter);
        this.onSelectedItemsChanged();
    }

    protected getReportWidgetFilter(): IOnboardingReportWidgetFilter {
        return this.fiOnboardingReportHelperService.getReportWidgetFilter(this.widget());
    }

    protected onPeriodDateRangeChange(newRange: IOnboardingPeriodDateRange): void {
        this.fiOnboardingReportDateService.setNewDateRange(this.widget().Id, newRange);
    }

    protected saveWidgetRequest(widget?: IOnboardingReportWidget): Promise<number> {
        return this.fiOnboardingReportService.saveReportWidget(widget || this.widget());
    }

    protected resetReportContainer(): void {
        this.fiOnboardingReportDataService.resetReportContainer();
    }

    protected back(): void {
        window.history.back();
    }

    protected readonly timeIntervalOptions: DeepReadonly<INameIdObj[]> =
        this.fiOnboardingReportHelperService.onboardingReportDateOptions;
    protected readonly FiOnboardingReportWidgetType = FiOnboardingReportWidgetType;
    protected readonly FiTaskReportDateType = FiTaskReportDateType;
    protected readonly FiOnboardingReportDateType = FiOnboardingReportDateType;
}
